import { type FC, useEffect, useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { BsArrowsCollapse, BsArrowsExpand, BsPlus } from 'react-icons/bs';
import { DragDropContext } from 'react-beautiful-dnd';
import DelayedDroppableWrapper from '@editor2/components/DelayedDroppableWrapper';
import cx from 'clsx';

import { THEME_COLORS } from '@soomo/lib/components/pageElements/Carousel/constants';

import Slide from './Slide';
import FigureProperties from './FigureProperties';
import CopyInput from '@editor2/components/CopyInputWrapper';
import IconButton from '@editor2/components/IconButton';

import { carouselStyles } from './styles';
import { elementCommonStyles } from '@editor2/styles/element';

import { carouselToCarouselProperties } from '@editor2/mappers/element';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';

import { type Carousel } from '@editor2/types/pageElements';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { useCarouselSlides } from './useCarouselSlides';

import SlidePreview from './SlidePreview';
import { CollapseState } from '@editor2/types/collapseState';
import { CarouselTheme } from '@soomo/lib/types/index';
import { type RootState } from '@editor2/reducers/root';
import { useSelector } from 'react-redux';

interface Props {
	element: Carousel;
	courseId: number;
}

const CarouselElement: FC<Props> = (props) => {
	const { element, courseId } = props;

	const commonPayload = useMemo(() => {
		return { elementId: element.family_id, courseId };
	}, [element.family_id, courseId]);
	const initProperties = carouselToCarouselProperties(element);

	const [carouselProperties, setCarouselProperties] = useState({ ...initProperties });
	const [expandAll, setExpandAll] = useState<CollapseState>(CollapseState.CollapseAll);

	const { collapseState } = useSelector((state: RootState) => state.courses[courseId]);

	const debouncedDispatch = useDebouncedDispatch();

	const {
		slides,
		setAllSlides,
		handleAddSlide,
		handleDuplicateSlide,
		handleDeleteSlide,
		handleSlideChange
	} = useCarouselSlides(initProperties.slides);

	useEffect(() => {
		if (collapseState.elements === CollapseState.RevealAllHTML) {
			setExpandAll(CollapseState.RevealAllHTML);
		}
	}, [collapseState.elements]);

	useEffect(() => {
		setCarouselProperties((prev) => ({ ...prev, slides }));
		debouncedDispatch(updateElement({ ...commonPayload, slides }));
	}, [slides, commonPayload, debouncedDispatch]);

	useEffect(() => {
		const updatedCarouselProperties = carouselToCarouselProperties(element);
		setCarouselProperties((prev) => ({ ...prev, ...updatedCarouselProperties }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [element]);

	const handleChange = (value) => {
		setCarouselProperties({ ...carouselProperties, ...value });
		debouncedDispatch(updateElement({ ...commonPayload, ...value }));
	};

	const onDragEnd = (result) => {
		const { source, destination } = result;
		const { droppableId: sourceDroppableId, index: sourceIndex } = source;
		const { droppableId: destinationDroppableId, index: destinationIndex } = destination;

		if (sourceDroppableId !== destinationDroppableId) return;

		const draggableSlide = slides[sourceIndex];
		const tempSlides = [...slides.slice(0, sourceIndex), ...slides.slice(sourceIndex + 1)];
		const updatedSlides = [
			...tempSlides.slice(0, destinationIndex),
			draggableSlide,
			...tempSlides.slice(destinationIndex)
		];

		setAllSlides(updatedSlides);
	};

	return (
		<Form className={cx(carouselStyles, elementCommonStyles)}>
			<CopyInput label="Family ID" value={carouselProperties.family_id} />
			<FigureProperties element={element} courseId={courseId} />

			<div className="theme-colors">
				<Form.Group>
					<Form.Label>Theme</Form.Label>

					<Form.Control
						as="select"
						className="select"
						value={carouselProperties.theme}
						onChange={(e) =>
							handleChange({
								theme: e.target.value,
								color: THEME_COLORS[e.target.value][0],
								logo_url: ''
							})
						}>
						{Object.values(CarouselTheme).map((type) => (
							<option key={type} value={type}>
								{type} content
							</option>
						))}
					</Form.Control>
				</Form.Group>
				<Form.Group className="color-boxes-block">
					<Form.Label>
						{carouselProperties.theme === CarouselTheme.Soomo ? 'Main' : 'Accent'} color
					</Form.Label>
					<div className="color-boxes">
						{THEME_COLORS[carouselProperties.theme].map((hex) => (
							<div
								key={hex}
								role="button"
								className={`color-box ${carouselProperties.color === hex && 'selected'}`}
								style={{ background: hex }}
								onClick={() => handleChange({ color: hex })}
							/>
						))}
					</div>
				</Form.Group>
			</div>

			{carouselProperties.theme === CarouselTheme.Permissioned && (
				<Form.Group>
					<Form.Label>Logo image URL</Form.Label>
					<Form.Control
						value={carouselProperties.logo_url}
						onChange={(e) => handleChange({ logo_url: e.target.value })}
					/>
					<Form.Label>Logo image alt text</Form.Label>
					<Form.Control
						value={carouselProperties.logo_alt}
						onChange={(e) => handleChange({ logo_alt: e.target.value })}
					/>
				</Form.Group>
			)}

			<div className="slides-grid-preview">
				{slides.map((slide) => (
					<SlidePreview
						key={slide.family_id}
						slide={slide}
						theme={carouselProperties.theme}
						color={carouselProperties.color}
						logo_url={carouselProperties.logo_url}
						logo_alt={carouselProperties.logo_alt}
						scale={0.4}
					/>
				))}
			</div>

			<Form.Group>
				<div className="slides-container-header">
					<div>
						<span className="label">Slides ({slides.length} total)</span>
						<IconButton className="add-slide-button" onClick={handleAddSlide}>
							<BsPlus />
						</IconButton>
					</div>

					<div>
						<Button
							variant="outline-secondary"
							className="options-button"
							onClick={() => setExpandAll(CollapseState.RevealAllHTML)}>
							<BsArrowsExpand />
							Reveal All HTML
						</Button>
						<Button
							variant="outline-secondary"
							className="options-button"
							onClick={() => setExpandAll(CollapseState.ExpandAll)}>
							<BsArrowsExpand />
							Expand All Slides
						</Button>
						<Button
							variant="outline-secondary"
							className="options-button"
							onClick={() => setExpandAll(CollapseState.CollapseAll)}>
							<BsArrowsCollapse />
							Collapse All Slides
						</Button>
					</div>
				</div>

				<div className="slides-container">
					<DragDropContext onDragEnd={onDragEnd}>
						<DelayedDroppableWrapper droppableId={'slides-panel'} type="TimelineSlide">
							{(provided) => (
								<div ref={provided.innerRef} {...provided.droppableProps}>
									{slides.map((slide, index) => (
										<Slide
											key={slide.family_id}
											slide={slide}
											index={index}
											expandAll={expandAll}
											setExpandAll={setExpandAll}
											theme={carouselProperties.theme}
											color={carouselProperties.color}
											logo_url={carouselProperties.logo_url}
											handleSlideChange={handleSlideChange}
											handleAddSlide={handleAddSlide}
											handleDuplicateSlide={handleDuplicateSlide}
											handleDeleteSlide={handleDeleteSlide}
										/>
									))}
									{provided.placeholder}
								</div>
							)}
						</DelayedDroppableWrapper>
					</DragDropContext>
				</div>
			</Form.Group>
		</Form>
	);
};

export default CarouselElement;
